import { effectiveDialogueVoiceId } from './characterVoices.js';

// Portable render identity. Authority and deployment UUIDs are separate.
export const PREPARATION_LIMITS = Object.freeze({ lines: 16, text: 240, bytes: 16 * 1024 * 1024, objects: 48, jobs: 16, lifetime: 86400000, generationsPerHour: 32 });
// Deployment opt-in; does not increase the budget of any existing service.
export const SCRIPT_PREPARATION_LIMITS = Object.freeze({ ...PREPARATION_LIMITS, lines: 1000, text: 4000,
  parts: 6000, bytes: 4 * 1024 ** 3, objects: 8192, generationsPerHour: 1200 });
export const preparationLimits = env => env?.QLINE_SPEECH_CAPACITY === 'script-v1' ? SCRIPT_PREPARATION_LIMITS : PREPARATION_LIMITS;
export const PREPARED_LINE_BYTES = 32 * 1024 * 1024;
export const PREPARATION_INPUT_BYTES = 512 * 1024;

// Keep every code unit, including whitespace. Prefer sentence/word boundaries
// in the last fifth of a part so a 4,000-character cue remains at most 22 parts.
export function splitSpeechText(text) {
  if (typeof text !== 'string' || !text.trim() || text.length > 4000) throw new Error('Speech needs 1–4,000 characters. No text was shortened.');
  const parts = []; let offset = 0;
  while (offset < text.length) {
    let end = Math.min(offset + 240, text.length);
    if (end < text.length) {
      const candidate = text.slice(offset + 192, end);
      const sentence = [...candidate.matchAll(/[.!?;:]\s+/g)].at(-1);
      const space = [...candidate.matchAll(/\s+/g)].at(-1);
      const boundary = sentence || space;
      if (boundary) end = offset + 192 + boundary.index + boundary[0].length;
      if (/[\uD800-\uDBFF]/.test(text[end - 1]) && /[\uDC00-\uDFFF]/.test(text[end])) end--;
    }
    const part = text.slice(offset, end);
    if (!part.trim()) throw new Error('A long whitespace-only span cannot be spoken. Correct that span before preparation; no text was discarded.');
    parts.push(part); offset = end;
  }
  return parts;
}
export const RENDER_SETTINGS = Object.freeze({ language: 'en', engine: 'kokoro', speed: 1, normalize: true, effects: [], personality: false, maxChunkChars: 240, crossfadeMs: 0 });
export const MODEL_REVISION = 'f3ff3571791e39611d31c381e3a41a3af07b4987';
export function preparationSnapshot(scenario) {
  return { projectId: scenario.id, lines: (scenario.cues || []).filter(c => c.type === 'dialogue').map(c => ({
    id: c.id, characterId: c.characterId, text: c.dialogueText ?? c.text,
    voiceId: effectiveDialogueVoiceId(scenario, c.characterId),
  })) };
}
export async function speechHash(value) {
  const bytes = value instanceof ArrayBuffer || ArrayBuffer.isView(value) ? value : new TextEncoder().encode(JSON.stringify(value));
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), b => b.toString(16).padStart(2, '0')).join('');
}
export function verifiedRender(support, voiceId) {
  const r = support.renderIdentity, hash = v => typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);
  if (!support.available || !support.voices?.some(v => v.id === voiceId) || support.provider !== 'Voicebox' || support.model !== 'hexgrad/Kokoro-82M' ||
      r?.revision !== MODEL_REVISION || !hash(r.modelSha256) || !hash(r.configSha256) || !hash(r.voices?.[voiceId]) || typeof r.runtime !== 'string' || r.runtime.length > 200) {
    throw Object.assign(new Error('Preparation needs a ready Voicebox service with verified model, runtime and preset revisions.'), { status: 503 });
  }
  return { provider: 'voicebox', model: support.model, revision: r.revision, modelSha256: r.modelSha256,
    configSha256: r.configSha256, runtime: r.runtime, voiceId, voiceSha256: r.voices[voiceId], settings: RENDER_SETTINGS };
}
export const speechContentKey = (text, render) => speechHash({ text, render });
